/** `File.type` is empty for several formats a browser will not name; a text file is the common one. */
const TEXT_MIME_TYPE_PREFIX = 'text/';

/**
 * MIME types Clarity treats as text even though they are not under `text/`.
 *
 * Its own list, not `FILE_TYPES`: the question here is "can this be read as text", which is wider than
 * the upload table's document category — a `.ts` file is text and is not a document.
 */
const TEXT_MIME_TYPES: readonly string[] = [
	'text/plain',
	'text/markdown',
	'text/csv',
	'text/html',
	'text/css',
	'text/javascript',
	'application/json',
	'application/javascript',
	'application/typescript',
	'text/x-python',
	'text/x-java-source',
	'application/x-httpd-php',
	'application/xml',
	'text/xml',
	'application/yaml',
	'text/yaml',
];

/** Extensions a browser reports no MIME type for but whose content is still text. */
const TEXT_EXTENSIONS: readonly string[] = [
	'.md',
	'.txt',
	'.csv',
	'.log',
	'.js',
	'.jsx',
	'.ts',
	'.tsx',
	'.json',
	'.html',
	'.css',
	'.scss',
	'.less',
	'.py',
	'.ipynb',
	'.java',
	'.kt',
	'.kotlin',
	'.c',
	'.cpp',
	'.h',
	'.hpp',
	'.cs',
	'.go',
	'.rs',
	'.rust',
	'.php',
	'.rb',
	'.ruby',
	'.sh',
	'.bash',
	'.zsh',
	'.fish',
	'.sql',
	'.yaml',
	'.yml',
	'.toml',
	'.xml',
	'.swift',
	'.m',
	'.mm',
	'.dockerfile',
	'dockerfile',
	'.gitignore',
	'.env',
];

/** Ported from Clarity `apps/web/src/shared/utils/fileUtils.ts` (commit `9015ce88c`). */
/**
 * Whether the file's content can be read as text.
 *
 * Wider than the document category on purpose — source code is text and is not a document — and it
 * matches on the extension as well as the MIME type because a browser reports `''` for most of the
 * extensions above.
 *
 * @param file The file the reader attached.
 * @returns `true` when the send path may read this file as text.
 */
export function isClarityChatTextBasedFile(file: File): boolean {
	const name = file.name.toLowerCase();

	return (
		file.type.startsWith(TEXT_MIME_TYPE_PREFIX) ||
		TEXT_MIME_TYPES.includes(file.type) ||
		TEXT_EXTENSIONS.some(extension => name.endsWith(extension))
	);
}
